import React from 'react';
import { EditIcon, DatabaseIcon, PackageIcon, CheckIcon, XIcon, DownloadIcon, BuildingIcon, HomeIcon } from 'lucide-react';
import { Catalog } from '../CatalogsManagement';
interface CatalogCardProps {
  catalog: Catalog;
  onEdit: () => void;
  onManageSchema: () => void;
  onManageProducts: () => void;
  onToggleStatus: () => void;
  locale: string;
}
export function CatalogCard({
  catalog,
  onEdit,
  onManageSchema,
  onManageProducts,
  onToggleStatus,
  locale
}: CatalogCardProps) {
  // Format date based on locale
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No End Date';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };
  return <div className="bg-white shadow rounded-lg overflow-hidden border border-gray-200 hover:shadow-md transition-shadow duration-200" data-id="element-10932">
      {/* Card Header */}
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200 bg-gray-50" data-id="element-10933">
        <div className="flex justify-between items-start" data-id="element-10934">
          <div data-id="element-10935">
            <h3 className="text-lg leading-6 font-medium text-gray-900 truncate" title={catalog.name} data-id="element-10936">
              {catalog.name}
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500 truncate" title={catalog.description} data-id="element-10937">
              {catalog.description}
            </p>
          </div>
          <div data-id="element-10938">
            {catalog.isActive ? <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800" data-id="element-10939">
                <CheckIcon className="h-4 w-4 mr-1" data-id="element-10940" />
                Active
              </span> : <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800" data-id="element-10941">
                <XIcon className="h-4 w-4 mr-1" data-id="element-10942" />
                Inactive
              </span>}
          </div>
        </div>
      </div>
      {/* Card Body */}
      <div className="px-4 py-5 sm:p-6" data-id="element-10943">
        <div className="grid grid-cols-2 gap-4" data-id="element-10944">
          <div data-id="element-10945">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10946">
              Vendor
            </h4>
            <p className="mt-1 text-sm text-gray-900" data-id="element-10947">{catalog.vendorName}</p>
          </div>
          <div data-id="element-10948">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10949">
              Products
            </h4>
            <p className="mt-1 text-sm text-gray-900" data-id="element-10950">{catalog.productCount}</p>
          </div>
          <div data-id="element-10951">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10952">
              Active From
            </h4>
            <p className="mt-1 text-sm text-gray-900" data-id="element-10953">
              {formatDate(catalog.activeFrom)}
            </p>
          </div>
          <div data-id="element-10954">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10955">
              Active To
            </h4>
            <p className="mt-1 text-sm text-gray-900" data-id="element-10956">
              {formatDate(catalog.activeTo)}
            </p>
          </div>
        </div>
        <div className="mt-4" data-id="element-10957">
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10958">
            Scope
          </h4>
          <div className="mt-1 flex flex-wrap gap-2" data-id="element-10959">
            <div className="flex items-center text-xs text-gray-700" data-id="element-10960">
              <BuildingIcon className="h-3 w-3 mr-1 text-gray-400" data-id="element-10961" />
              {catalog.entities.length}{' '}
              {catalog.entities.length === 1 ? 'Entity' : 'Entities'}
            </div>
            <div className="flex items-center text-xs text-gray-700" data-id="element-10962">
              <HomeIcon className="h-3 w-3 mr-1 text-gray-400" data-id="element-10963" />
              {catalog.sites.length}{' '}
              {catalog.sites.length === 1 ? 'Site' : 'Sites'}
            </div>
          </div>
        </div>
      </div>
      {/* Card Footer */}
      <div className="px-4 py-4 sm:px-6 bg-gray-50 border-t border-gray-200" data-id="element-10964">
        <div className="flex justify-between" data-id="element-10965">
          <button onClick={onManageProducts} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-10966">
            <PackageIcon className="h-4 w-4 mr-1" data-id="element-10967" />
            Products
          </button>
          <div className="flex space-x-2" data-id="element-10968">
            <button onClick={onEdit} className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-indigo-600 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" title="Edit Catalog" data-id="element-10969">
              <EditIcon className="h-4 w-4" data-id="element-10970" />
            </button>
            <button onClick={onManageSchema} className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-indigo-600 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" title="Manage Schema" data-id="element-10971">
              <DatabaseIcon className="h-4 w-4" data-id="element-10972" />
            </button>
            <button onClick={onToggleStatus} className={`inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${catalog.isActive ? 'text-red-600 bg-red-50 hover:bg-red-100 focus:ring-red-500' : 'text-green-600 bg-green-50 hover:bg-green-100 focus:ring-green-500'}`} title={catalog.isActive ? 'Deactivate' : 'Activate'} data-id="element-10973">
              {catalog.isActive ? <XIcon className="h-4 w-4" data-id="element-10974" /> : <CheckIcon className="h-4 w-4" data-id="element-10975" />}
            </button>
            <button className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-gray-600 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500" title="Export Products" data-id="element-10976">
              <DownloadIcon className="h-4 w-4" data-id="element-10977" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}